import { Empty } from 'antd';
import { useNavigate } from 'react-router-dom';

import { formatVolume } from '../../../utils/formatters';
import { locationLabel } from '../locations';
import type { RecentMovement } from '../../../types/dashboard';

/** Символ і тон операції. Іконки — текстові, як у прототипі. */
const OP_ICONS: Record<string, { icon: string; color: string; bg: string }> = {
  purchase_round: { icon: '↓', color: '#175CD3', bg: '#EAF1FE' },
  sawing_in: { icon: '⇉', color: '#8B3A0F', bg: '#F7EBDD' },
  sawing_out: { icon: '↑', color: '#2E6B3E', bg: '#EAF2EA' },
  sawing_waste: { icon: '⇉', color: '#8A8272', bg: '#F1EFE9' },
  purchase_raw: { icon: '↓', color: '#175CD3', bg: '#EAF1FE' },
  purchase_dry: { icon: '↓', color: '#175CD3', bg: '#EAF1FE' },
  drying_in: { icon: '⇉', color: '#B45309', bg: '#FDF0D9' },
  drying_out: { icon: '↑', color: '#2E6B3E', bg: '#EAF2EA' },
  processing_in: { icon: '⇉', color: '#8B3A0F', bg: '#F7EBDD' },
  processing_out: { icon: '↑', color: '#2E6B3E', bg: '#EAF2EA' },
  processing_waste: { icon: '⇉', color: '#8A8272', bg: '#F1EFE9' },
  manufacturing: { icon: '⇉', color: '#8B3A0F', bg: '#F7EBDD' },
  shipment: { icon: '↑', color: '#2E6B3E', bg: '#EAF2EA' },
  transfer: { icon: '⇄', color: '#8A8272', bg: '#F1EFE9' },
  adjustment: { icon: '⇄', color: '#175CD3', bg: '#EAF1FE' },
  reversal: { icon: '✕', color: '#B42318', bg: '#FEF3F2' },
  cancellation: { icon: '✕', color: '#B42318', bg: '#FEF3F2' },
};

const FALLBACK = { icon: '·', color: '#8A8272', bg: '#F1EFE9' };

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'щойно';
  if (m < 60) return `${m} хв тому`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} год тому`;
  const d = Math.floor(h / 24);
  if (d === 1) return 'вчора';
  if (d < 7) return `${d} дн. тому`;
  return new Date(iso).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit' });
}

/** Скільки операцій показувати. Більше — і колонка переростає сітку камер. */
const VISIBLE_COUNT = 5;

/** Позицій: 1 позиція / 2 позиції / 5 позицій. */
function positionsWord(n: number): string {
  const last = n % 10;
  const tens = n % 100;
  if (tens >= 11 && tens <= 14) return 'позицій';
  if (last === 1) return 'позиція';
  if (last >= 2 && last <= 4) return 'позиції';
  return 'позицій';
}

export function RecentMovementsFeed({ movements }: { movements: RecentMovement[] }) {
  const navigate = useNavigate();
  const visible = movements.slice(0, VISIBLE_COUNT);
  // Стрічка показує лише 5 останніх операцій — заголовок веде в повний журнал.
  const openJournal = () => navigate('/reports/movements');
  return (
    <div className="dash-card">
      <div
        className="dash-card-head dash-card-head--link"
        style={{ marginBottom: 6 }}
        role="button"
        tabIndex={0}
        onClick={openJournal}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openJournal();
          }
        }}
      >
        <span className="dash-section-label">Останні операції</span>
        <span className="dash-link">Весь журнал →</span>
      </div>

      {visible.length === 0 ? (
        <Empty description="Поки що операцій немає" />
      ) : (
        visible.map((m) => {
          const meta = OP_ICONS[m.operation_type] ?? FALLBACK;
          const route = [m.from_location, m.to_location]
            .filter((loc) => loc && loc !== 'external')
            .map(locationLabel)
            .join(' → ');
          return (
            <div key={m.id} className="dash-feed-row">
              <span
                className="dash-feed-icon"
                style={{ background: meta.bg, color: meta.color }}
                aria-hidden
              >
                {meta.icon}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="dash-feed-title">
                  {m.operation_display} ·{' '}
                  {m.unit && m.unit !== 'м³'
                    ? `${m.volume_m3} ${m.unit}`
                    : formatVolume(m.volume_m3, { compact: true })}
                </div>
                <div className="dash-feed-sub">
                  {/* Одна позиція — її назва; кілька — лічильник, бо назва
                      першої з п'яти виглядала б так, ніби везли лише її. */}
                  {m.material_name ?? `${m.positions} ${positionsWord(m.positions)}`}
                  {route ? ` · ${route}` : ''}
                </div>
                {m.user_name && <div className="dash-feed-by">{m.user_name}</div>}
              </div>
              <span className="dash-feed-time">{timeAgo(m.timestamp)}</span>
            </div>
          );
        })
      )}
    </div>
  );
}
